import { createContext, useContext, useState, useCallback } from 'react';
import { dayjs, toZonedTime, type Dayjs } from '@vitvio/helpers';
import { useUserContext } from '@vitvio/user-context';

interface MissionControlCalendarContextType {
  selectedDate: Dayjs | null;
  setSelectedDate: (date: Dayjs | null) => void;
}

const MissionControlCalendarContext = createContext<MissionControlCalendarContextType | null>(null);

interface MissionControlCalendarProviderProps {
  children: React.ReactNode;
}

function MissionControlCalendarProvider({ children }: MissionControlCalendarProviderProps) {
  const { hospitalTimezone } = useUserContext();

  const zonedTime = toZonedTime(dayjs(), hospitalTimezone);
  const [selectedDate, setSelectedDateInternal] = useState<Dayjs | null>(zonedTime);

  // Convert incoming date to hospital timezone, keeping the same calendar date
  const setSelectedDate = useCallback(
    (date: Dayjs | null) => {
      // Keep same local time (midnight) but in hospital timezone
      setSelectedDateInternal(date?.startOf('day').tz(hospitalTimezone, true) ?? null);
    },
    [hospitalTimezone],
  );

  return (
    <MissionControlCalendarContext.Provider value={{ selectedDate, setSelectedDate }}>
      {children}
    </MissionControlCalendarContext.Provider>
  );
}

function useMissionControlCalendarContext() {
  const context = useContext(MissionControlCalendarContext);
  if (context === null) throw new Error('Mission Control Calendar Context was used outside the provider.');
  return context;
}

export { useMissionControlCalendarContext, MissionControlCalendarProvider };
